import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import { useTranslation } from 'src/arb-wrapper';
import { FormInput } from 'src/components/common/forms/FormInput';
import { type Control, useWatch } from 'react-hook-form';
import { OPTIONAL_PRODUCT_SET_PUBLISHERS } from 'src/constants';
import { type CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';

interface FormAutoGenerateControlsProps {
  disabled?: boolean;
  isEnabled: boolean;
  onToggle: (enabled: boolean) => void;
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<any>;
  indexedName: string;
  isValidating?: boolean;
  publisher: CampaignDesiredStateDto['publisher'];
  validationState: {
    isLoading: boolean;
    isValid: boolean | null;
    error: string | null;
  };
}

export const FormAutoGenerateControls: React.FC<FormAutoGenerateControlsProps> = ({
  disabled = false,
  isEnabled,
  onToggle,
  index,
  control,
  indexedName,
  isValidating = false,
  publisher,
  validationState,
}) => {
  const { t } = useTranslation();

  const productSetId = useWatch({
    control,
    name: `${indexedName}.productSetId`,
  });

  const productSetValidator = () => {
    // If auto-generation is enabled, always valid
    if (isEnabled) return true;
    // If no product set ID provided, check if it's required
    if (!productSetId) {
      return OPTIONAL_PRODUCT_SET_PUBLISHERS.includes(publisher);
    }
    // If product set ID is provided, validate it
    if (validationState.isLoading || isValidating) return false;
    return validationState.isValid === true;
  };

  const getProductSetErrorMessage = () => {
    // If auto-generation is enabled, no errors
    if (isEnabled) return undefined;
    // If no product set ID and it's required, show error
    if (!productSetId && !OPTIONAL_PRODUCT_SET_PUBLISHERS.includes(publisher)) {
      return t('product_set_validation_required');
    }
    // If product set ID is provided, show validation errors
    if (productSetId) {
      if (validationState.error) return validationState.error;
      if (validationState.isValid === false) return t('product_set_validation_invalid');
    }
    return undefined;
  };

  return (
    <Column spacing="200">
      <Row
        alignmentHorizontal="justify"
        alignmentVertical="top"
        spacing="400"
        widths={['30%', '70%']}
      >
        <Column spacing="100">
          <Text type="b200">{t('auto_generate_product_set_label')}</Text>
          <Toggle
            checked={isEnabled}
            data-testid={`auto-generate-product-set-${index}`}
            disabled={disabled}
            onChange={onToggle}
          >
            {isEnabled ? t('auto_generate_enabled') : t('auto_generate_disabled')}
          </Toggle>
        </Column>
        {control && !isEnabled && (
          <Column spacing="100">
            <FormInput
              control={control}
              data-testid={`product-set-id-input-${index}`}
              isValidating={isValidating}
              label={t('product_set_label')}
              name={`${indexedName}.productSetId`}
              placeholder={t('product_set_placeholder', { publisher })}
              validator={{
                fn: productSetValidator,
                message: getProductSetErrorMessage(),
              }}
            />
          </Column>
        )}
      </Row>
      {isEnabled
        ? (
        <Text color="secondary" type="b100">
          {t('auto_generate_explanation')}
        </Text>
          )
        : (
        <Text color="secondary" type="b100">
          {t('product_set_manual_explanation')}
        </Text>
          )}
    </Column>
  );
};
